import loadable from '@loadable/component';
import BpkImage, {
  BORDER_RADIUS_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import {
  BpkBox,
  BpkBreakpoint,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkPrice, {
  ALIGNS,
} from '@skyscanner-internal/backpack-web/bpk-component-price';
import BpkRating, {
  RATING_SIZES,
  RATING_SCALES,
} from '@skyscanner-internal/backpack-web/bpk-component-rating';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classNames from 'classnames';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import { useMBDContext } from '../common/MBD/useMBDContext';

import Rating from './Rating';

import type { ProviderRatingBigProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ProviderRating';

import STYLES from './ProviderRatingBig.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

export const RATING_CLASS = {
  DES: 'des', // description
  BAR: 'bar',
} as const;

const ProviderRatingBig = (props: ProviderRatingBigProps) => {
  const { providerInfo, strings } = props;
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!providerInfo || !strings) {
    return null;
  }

  if (
    providerInfo.ratingClass === RATING_CLASS.DES &&
    !providerInfo.providerDescription
  ) {
    return null;
  }

  const overallRating = providerInfo.ratingInfo?.overall || 0;

  return (
    <BpkVessel
      className={classNames(STYLES.ProviderRatingBig, {
        [STYLES.ProviderRatingBig__des]:
          providerInfo.ratingClass === RATING_CLASS.DES,
      })}
      data-testid="provider-rating-big"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <BpkBox
        display={providerInfo.price ? 'block' : 'flex'}
        minWidth={{ [BpkBreakpoint.Tablet]: '20rem' }}
      >
        <BpkBox
          display="flex"
          alignItems="center"
          aria-label={providerInfo.ratingInfo?.ariaLabel}
          role="status"
          data-testid="provider-rating-big-logo-rating"
        >
          <BpkImage
            className={STYLES.ProviderRatingBig__logo}
            src={providerInfo.src}
            altText={providerInfo.providerName}
            aspectRatio={1}
            borderRadiusStyle={BORDER_RADIUS_STYLES.sm}
          />
          {!overallRating ? (
            <BpkBox display="flex" flexDirection="column" minWidth="6.25rem">
              <BpkText textStyle={TEXT_STYLES.label1} tagName="span">
                {strings.noRatingYet}
              </BpkText>
              {providerInfo.ratingInfo?.reviews &&
                providerInfo.ratingInfo.reviews > 1 &&
                providerInfo.showReviews && (
                  <BpkText
                    color={TEXT_COLORS.textSecondary}
                    className={STYLES.ProviderRatingBig__reviews}
                    tagName="span"
                  >
                    {providerInfo.ratingInfo?.reviewsNumber}
                  </BpkText>
                )}
            </BpkBox>
          ) : (
            <BpkRating
              ariaLabel={providerInfo.ratingInfo?.ratingTitle || ''}
              title={providerInfo.ratingInfo?.ratingTitle}
              size={RATING_SIZES.large}
              ratingScale={RATING_SCALES.zeroToFive}
              subtitle={
                providerInfo.ratingInfo?.reviews &&
                providerInfo.ratingInfo.reviews > 1
                  ? providerInfo.ratingInfo?.reviewsNumber
                  : ''
              }
              value={overallRating.toFixed(1)}
            />
          )}
        </BpkBox>
        {providerInfo.price && (
          <BpkVessel className={STYLES.ProviderRatingBig__price}>
            <BpkPrice
              align={ALIGNS.left}
              leadingText={strings.from}
              price={providerInfo.price}
              trailingText={strings.perDay}
            />
          </BpkVessel>
        )}
      </BpkBox>
      <BpkVessel
        className={classNames(STYLES.ProviderRatingBig__ratings, {
          [STYLES.ProviderRatingBig__hideOnMobile]:
            !overallRating && providerInfo.ratingClass === RATING_CLASS.BAR,
        })}
      >
        <BpkVessel className={STYLES.ProviderRatingBig__divided} />
        {providerInfo.ratingClass === RATING_CLASS.DES && (
          <BpkBox display="flex" flexDirection="column" justifyContent="center">
            <BpkText
              tagName="span"
              textStyle={TEXT_STYLES.footnote}
              className={STYLES.ProviderRatingBig__providerDescription}
              aria-label={providerInfo.providerDescription}
              role="status"
            >
              {providerInfo.providerDescription}
            </BpkText>
          </BpkBox>
        )}
        {providerInfo.ratingClass === RATING_CLASS.BAR &&
          providerInfo.ratingInfo &&
          providerInfo.ratingInfo.items.length > 0 && (
            <BpkBox
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
              alignContent="space-around"
              width="100%"
            >
              {providerInfo.ratingInfo?.items.map((item, index) => {
                const itemMbdComponentName = `${rawComponentName}_item`;
                return (
                  <Rating
                    key={item.name}
                    rating={item.rating}
                    label={item.name}
                    ariaLabel={item.ariaLabel || ''}
                    data-tracking-element-id={`${itemMbdComponentName}_${index}`}
                    data-tracking-common-params={buildMBDParam(
                      itemMbdComponentName,
                      { item_name: item.name },
                    )}
                  />
                );
              })}
            </BpkBox>
          )}
      </BpkVessel>
      {providerInfo.price && (
        <BpkVessel className={STYLES.ProviderRatingBig__footerPrice}>
          <BpkPrice
            align={ALIGNS.left}
            leadingText={strings.from}
            price={providerInfo.price}
            trailingText={strings.perDay}
          />
        </BpkVessel>
      )}
    </BpkVessel>
  );
};

export default (props: ProviderRatingBigProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.PROVIDER_RATING_BIG,
      })
    }
  >
    <ProviderRatingBig {...props} />
  </IntersectionObserverWrapper>
);
